/**
 * Details!-style meter rows. Two scopes:
 *  - 'current': the current fight. A new segment starts when the party enters combat, so the meters
 *    reset at every pull; after combat ends the last fight stays up until the next pull.
 *  - 'overall': the whole run. Prefers the sim's CombatLog.meters(); falls back to the local
 *    aggregation of step() events.
 */
import type { CombatLog, DeepReadonly, Meters, SimEvent, UnitId, World } from '@keystone/sim';

export type MeterMode = 'damage' | 'healing' | 'taken' | 'interrupts';
export type MeterScope = 'current' | 'overall';
export const METER_MODES: readonly MeterMode[] = ['damage', 'healing', 'taken', 'interrupts'];

export interface MeterRow {
  unitId: UnitId;
  name: string;
  value: number;
  /** Per second of party combat time (counts for interrupts). */
  perSec: number;
  color: string;
}

export interface Totals {
  damage: number;
  healing: number;
  taken: number;
  interrupts: number;
  deaths: number;
}

export class MeterTracker {
  private totals = new Map<UnitId, Totals>();
  /** The current (or, out of combat, the last) fight. */
  private segment = new Map<UnitId, Totals>();
  /** Party combat time (ms), tracked locally for per-second rates. */
  combatMs = 0;
  /** Combat time of the current segment (ms). */
  segmentMs = 0;
  /** Fights started so far (0 before the first pull). */
  segments = 0;
  /** The party is in combat (as of the last ingest). */
  inCombat = false;

  ingest(events: readonly SimEvent[], world: DeepReadonly<World>, tickMs: number): void {
    const was = this.inCombat;
    this.inCombat = world.partyIds.some((id) => world.units[id]?.inCombat);
    if (this.inCombat && !was) {
      // A new pull: the current-fight meters start over.
      this.segment.clear();
      this.segmentMs = 0;
      this.segments++;
    }
    if (this.inCombat) {
      this.combatMs += tickMs;
      this.segmentMs += tickMs;
    }
    // Out-of-combat events (top-up heals between pulls) count for the run, not the frozen last fight.
    const seg = this.inCombat || was;
    for (const e of events) {
      if (e.type === 'damage') {
        this.add(e.sourceID, 'damage', e.amount + e.absorbed, seg);
        this.add(e.targetID, 'taken', e.amount + e.absorbed, seg);
      } else if (e.type === 'heal') this.add(e.sourceID, 'healing', e.amount, seg);
      else if (e.type === 'absorbed') this.add(e.sourceID, 'healing', e.amount, seg);
      else if (e.type === 'interrupt') this.add(e.sourceID, 'interrupts', 1, seg);
      else if (e.type === 'death') this.add(e.targetID, 'deaths', 1, seg);
    }
  }

  private add(id: UnitId | undefined, k: keyof Totals, v: number, seg: boolean): void {
    if (!id) return;
    bump(this.totals, id, k, v);
    if (seg) bump(this.segment, id, k, v);
  }

  get(id: UnitId, scope: MeterScope = 'overall'): Totals | undefined {
    return (scope === 'current' ? this.segment : this.totals).get(id);
  }
}

function bump(map: Map<UnitId, Totals>, id: UnitId, k: keyof Totals, v: number): void {
  let t = map.get(id);
  if (!t) map.set(id, (t = { damage: 0, healing: 0, taken: 0, interrupts: 0, deaths: 0 }));
  t[k] += v;
}

/** Value for a mode out of the log's UnitMeters. */
function fromLog(m: Meters, id: UnitId, mode: MeterMode): number | undefined {
  const u = m.units[id];
  if (!u) return undefined;
  switch (mode) {
    case 'damage':
      return u.damageDone;
    case 'healing':
      return u.healingDone + u.absorbsDone;
    case 'taken':
      return u.damageTaken;
    case 'interrupts':
      return u.interrupts;
  }
}

function safeLogMeters(log: CombatLog | undefined): Meters | null {
  try {
    const m = log?.meters();
    return m && Object.keys(m.units).length > 0 ? m : null;
  } catch {
    return null;
  }
}

/** Party rows for a mode, sorted descending. */
export function meterRows(
  world: DeepReadonly<World>,
  mode: MeterMode,
  tracker: MeterTracker,
  log?: CombatLog,
  colorOf: (id: UnitId) => string = () => '#888',
  scope: MeterScope = 'overall',
): MeterRow[] {
  const current = scope === 'current';
  const logMeters = current ? null : safeLogMeters(log);
  const secs = Math.max(1, (current ? tracker.segmentMs : tracker.combatMs) / 1000);
  const rows: MeterRow[] = world.partyIds.map((id) => {
    const value = (logMeters ? fromLog(logMeters, id, mode) : undefined) ?? tracker.get(id, scope)?.[mode] ?? 0;
    return { unitId: id, name: world.units[id]?.name ?? id, value, perSec: mode === 'interrupts' ? value : value / secs, color: colorOf(id) };
  });
  return rows.sort((a, b) => b.value - a.value || (a.unitId < b.unitId ? -1 : 1));
}

/** One party member on the end-of-run scoreboard (whole run). */
export interface ScoreRow {
  unitId: UnitId;
  name: string;
  classId: string | null;
  color: string;
  damage: number;
  healing: number;
  taken: number;
  interrupts: number;
  deaths: number;
  /** Per second of party combat time. */
  dps: number;
  hps: number;
  /** The local player's hero. */
  you: boolean;
}

/** End-of-run scoreboard rows, in party order. Totals match the overall meters. */
export function scoreboard(world: DeepReadonly<World>, tracker: MeterTracker, log?: CombatLog, colorOf: (id: UnitId) => string = () => '#888', youId: UnitId | null = null): ScoreRow[] {
  const logMeters = safeLogMeters(log);
  const secs = Math.max(1, tracker.combatMs / 1000);
  const val = (id: UnitId, mode: MeterMode): number => (logMeters ? fromLog(logMeters, id, mode) : undefined) ?? tracker.get(id)?.[mode] ?? 0;
  return world.partyIds.map((id) => {
    const damage = val(id, 'damage');
    const healing = val(id, 'healing');
    return {
      unitId: id,
      name: world.units[id]?.name ?? id,
      classId: world.units[id]?.classId ?? null,
      color: colorOf(id),
      damage,
      healing,
      taken: val(id, 'taken'),
      interrupts: val(id, 'interrupts'),
      deaths: tracker.get(id)?.deaths ?? 0,
      dps: damage / secs,
      hps: healing / secs,
      you: id === youId,
    };
  });
}
